import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import type { ApiFailureError } from '../../lib/http/errors';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';

function defaultIsEmpty(data: unknown): boolean {
  return Array.isArray(data) && data.length === 0;
}

/** Loading, then error, then empty, then data — in that order, so a stale
 *  `data` beside a fresh error never renders as if the error had not happened.
 *  Without `empty` the caller has already decided what an empty result shows. */
export function QueryStates<T>(props: {
  query: UseQueryResult<T, ApiFailureError>;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}): ReactNode {
  const { query } = props;
  if (query.status === 'pending') return props.loading ?? <LoadingState />;
  if (query.status === 'error') {
    return <ErrorState failure={query.error.failure} onRetry={() => void query.refetch()} />;
  }
  if (props.empty !== undefined && (props.isEmpty ?? defaultIsEmpty)(query.data)) {
    return props.empty;
  }
  return props.children(query.data);
}
